import React, { useState, useEffect, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, CheckCircle, AlertCircle, Info, Heart } from 'lucide-react';
import { cn } from '../../../lib/utils';

/**
 * Toast Types for Islamic giving platform
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'blessing';

/**
 * Toast Position Options
 */
export type ToastPosition =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left'
  | 'top-center'
  | 'bottom-center';

/**
 * Toast Interface
 */
export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  position?: ToastPosition;
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Toast Context Type
 */
interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

/**
 * Toast Context
 */
const ToastContext = createContext<ToastContextType | undefined>(undefined);

/**
 * Toast Provider Props
 */
interface ToastProviderProps {
  children: React.ReactNode;
  position?: ToastPosition;
  maxToasts?: number;
}

/**
 * Toast Provider Component
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = 'top-right',
  maxToasts = 5
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = (toast: Omit<Toast, 'id'>): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: Toast = {
      ...toast,
      id,
      position: toast.position || position,
      duration: toast.duration || 5000
    };

    setToasts(prev => {
      const updated = [newToast, ...prev];
      return updated.slice(0, maxToasts);
    });

    return id;
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const clearToasts = () => {
    setToasts([]);
  };

  const value: ToastContextType = {
    toasts,
    addToast,
    removeToast,
    clearToasts
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer position={position} />
    </ToastContext.Provider>
  );
};

/**
 * Toast Container Component
 */
const ToastContainer: React.FC<{ position: ToastPosition }> = ({
  position
}) => {
  const { toasts, removeToast } = useToast();

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2'
  };

  return (
    <div
      className={cn(
        'pointer-events-none fixed z-50',
        positionClasses[position]
      )}
    >
      <div className="space-y-2">
        <AnimatePresence>
          {toasts.map(toast => (
            <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

/**
 * Individual Toast Item Component
 */
const ToastItem: React.FC<{
  toast: Toast;
  onRemove: (id: string) => void;
}> = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onRemove(toast.id), 300);
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.duration, toast.id, onRemove]);

  const getToastConfig = (type: ToastType) => {
    switch (type) {
      case 'success':
        return {
          icon: CheckCircle,
          bgColor: 'from-emerald-500 to-teal-500',
          borderColor: 'border-emerald-200 dark:border-emerald-700',
          textColor: 'text-emerald-800 dark:text-emerald-200',
          iconColor: 'text-emerald-600 dark:text-emerald-400'
        };
      case 'error':
        return {
          icon: AlertCircle,
          bgColor: 'from-red-500 to-rose-500',
          borderColor: 'border-red-200 dark:border-red-700',
          textColor: 'text-red-800 dark:text-red-200',
          iconColor: 'text-red-600 dark:text-red-400'
        };
      case 'warning':
        return {
          icon: AlertCircle,
          bgColor: 'from-amber-500 to-orange-500',
          borderColor: 'border-amber-200 dark:border-amber-700',
          textColor: 'text-amber-800 dark:text-amber-200',
          iconColor: 'text-amber-600 dark:text-amber-400'
        };
      case 'info':
        return {
          icon: Info,
          bgColor: 'from-blue-500 to-indigo-500',
          borderColor: 'border-blue-200 dark:border-blue-700',
          textColor: 'text-blue-800 dark:text-blue-200',
          iconColor: 'text-blue-600 dark:text-blue-400'
        };
      case 'blessing':
        return {
          icon: Heart,
          bgColor: 'from-gold-400 to-amber-500',
          borderColor: 'border-gold-200 dark:border-gold-700',
          textColor: 'text-gold-800 dark:text-gold-200',
          iconColor: 'text-gold-600 dark:text-gold-400'
        };
      default:
        return {
          icon: Info,
          bgColor: 'from-gray-500 to-slate-500',
          borderColor: 'border-gray-200 dark:border-gray-700',
          textColor: 'text-gray-800 dark:text-gray-200',
          iconColor: 'text-gray-600 dark:text-gray-400'
        };
    }
  };

  const config = getToastConfig(toast.type);
  const IconComponent = config.icon;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, x: 300, scale: 0.8 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 300, scale: 0.8 }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30,
            duration: 0.3
          }}
          className={cn(
            'pointer-events-auto w-full max-w-sm shadow-premium-glow',
            'rounded-2xl border bg-white backdrop-blur-sm dark:bg-gray-800',
            'transform transition-all duration-300 hover:scale-105',
            config.borderColor
          )}
        >
          <div className="p-4">
            <div className="flex items-start space-x-3">
              {/* Icon */}
              <div
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                  `bg-linear-to-r ${config.bgColor}`
                )}
              >
                <IconComponent className={cn('h-5 w-5 text-white')} />
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <h4
                      className={cn(
                        'font-jakarta text-sm font-bold leading-tight',
                        config.textColor
                      )}
                    >
                      {toast.title}
                    </h4>
                    {toast.message && (
                      <p className="mt-1 font-ui text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                        {toast.message}
                      </p>
                    )}
                  </div>

                  {/* Close Button */}
                  <button
                    onClick={() => {
                      setIsVisible(false);
                      setTimeout(() => onRemove(toast.id), 300);
                    }}
                    className="ml-2 shrink-0 rounded-lg p-1 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label="Close toast"
                  >
                    <X className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                  </button>
                </div>

                {/* Action Button */}
                {toast.action && (
                  <div className="mt-3">
                    <button
                      onClick={toast.action.onClick}
                      className={cn(
                        'rounded-lg px-3 py-1.5 text-xs font-semibold transition-all duration-200',
                        'bg-linear-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600',
                        'transform text-white shadow-md hover:scale-105 hover:shadow-lg'
                      )}
                    >
                      {toast.action.label}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Custom Hook for Toast Management
 */
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

/**
 * Toast Hook with Convenience Methods
 */
export const useToastActions = () => {
  const { addToast, removeToast, clearToasts } = useToast();

  const success = (
    title: string,
    message?: string,
    options?: Partial<Toast>
  ) => {
    return addToast({
      type: 'success',
      title,
      message,
      ...options
    });
  };

  const error = (title: string, message?: string, options?: Partial<Toast>) => {
    return addToast({
      type: 'error',
      title,
      message,
      ...options
    });
  };

  const warning = (
    title: string,
    message?: string,
    options?: Partial<Toast>
  ) => {
    return addToast({
      type: 'warning',
      title,
      message,
      ...options
    });
  };

  const info = (title: string, message?: string, options?: Partial<Toast>) => {
    return addToast({
      type: 'info',
      title,
      message,
      ...options
    });
  };

  const blessing = (
    title: string,
    message?: string,
    options?: Partial<Toast>
  ) => {
    return addToast({
      type: 'blessing',
      title,
      message,
      ...options
    });
  };

  return {
    success,
    error,
    warning,
    info,
    blessing,
    addToast,
    removeToast,
    clearToasts
  };
};
